import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { cn } from '@/utils';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  options: SearchableSelectOption[];
  onSelect: (option: SearchableSelectOption) => void;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  error?: boolean;
  emptyText?: string;
}

export function SearchableSelect({
  searchTerm,
  onSearchTermChange,
  options,
  onSelect,
  placeholder,
  isLoading,
  disabled,
  error,
  emptyText = 'No results found',
}: SearchableSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={searchTerm}
        onChange={(e) => {
          onSearchTermChange(e.target.value);
          setIsOpen(true);
        }}
        onFocus={(e) => {
          setIsOpen(true);
          e.target.select();
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className={cn(error && 'border-destructive')}
      />
      {isOpen && !disabled && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {isLoading ? (
            <div className="flex items-center gap-2 p-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading...
            </div>
          ) : options.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">{emptyText}</div>
          ) : (
            options.map((option) => (
              <button
                key={option.value}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect(option);
                  setIsOpen(false);
                }}
                className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
